import { Component, createElement, createRef, HTMLAttributes } from 'react';
import { PAGINATION_TIME_IN_MILLISECONDS } from '@shared/constants';

interface PaginatedItemsProps extends Pick<
  HTMLAttributes<HTMLElement>,
  'children' | 'className' | 'style' | 'aria-hidden'
> {
  itemIds: string[];
  component: 'div' | 'tbody';
}

interface ItemSnapshot {
  id: string;
  item: HTMLElement;
  overlay: HTMLElement;
  distance: string;
}

export default class PaginatedItems extends Component<PaginatedItemsProps> {
  private containerRef = createRef<HTMLElement>();
  private animations = new Map<string, () => void>();
  private containerResizeObserver?: ResizeObserver;

  componentDidMount() {
    window.addEventListener('resize', this.finishAnimations);
    window.addEventListener('scroll', this.finishAnimations, true);
    const container = this.getOverlayContainer();
    if (container && typeof ResizeObserver !== 'undefined') {
      let previousSize = container.getBoundingClientRect();
      this.containerResizeObserver = new ResizeObserver(() => {
        const size = container.getBoundingClientRect();
        if (
          size.width !== previousSize.width ||
          size.height !== previousSize.height
        ) {
          this.finishAnimations();
        }
        previousSize = size;
      });
      this.containerResizeObserver.observe(container, { box: 'border-box' });
    }
  }

  getSnapshotBeforeUpdate(): ItemSnapshot[] {
    const nextIds = new Set(this.props.itemIds);
    return this.getItems()
      .filter((item) => !nextIds.has(item.dataset.paginationId!))
      .map((item) => this.snapshot(item));
  }

  componentDidUpdate(
    previousProps: PaginatedItemsProps,
    _previousState: unknown,
    exitingItems: ItemSnapshot[],
  ) {
    if (document.documentElement.classList.contains('animations-paused')) {
      this.finishAnimations();
      return;
    }
    const previousIds = new Set(previousProps.itemIds);
    const enteringItems = this.getItems()
      .filter((item) => !previousIds.has(item.dataset.paginationId!))
      .map((item) => this.snapshot(item));

    for (const snapshot of exitingItems) {
      this.animate(snapshot, false);
    }
    for (const snapshot of enteringItems) {
      this.animate(snapshot, true);
    }
  }

  componentWillUnmount() {
    this.containerResizeObserver?.disconnect();
    window.removeEventListener('resize', this.finishAnimations);
    window.removeEventListener('scroll', this.finishAnimations, true);
    this.finishAnimations();
  }

  private finishAnimations = () => {
    for (const cleanup of this.animations.values()) {
      cleanup();
    }
  };

  private getItems() {
    return Array.from(this.containerRef.current?.children ?? []).filter(
      (item): item is HTMLElement =>
        item instanceof HTMLElement && item.dataset.paginationId !== undefined,
    );
  }

  private getOverlayContainer() {
    return this.props.component === 'tbody'
      ? this.containerRef.current?.parentElement?.parentElement
      : this.containerRef.current;
  }

  private snapshot(item: HTMLElement): ItemSnapshot {
    const rect = item.getBoundingClientRect();
    const clone = item.cloneNode(true) as HTMLElement;
    clone.style.visibility = 'visible';
    let overlay = clone;
    if (item instanceof HTMLTableRowElement) {
      const table = item.closest('table')!;
      overlay = table.cloneNode(false) as HTMLTableElement;
      const body = document.createElement('tbody');
      Array.from(item.cells).forEach((cell, index) => {
        const width = `${cell.getBoundingClientRect().width}px`;
        Object.assign((clone as HTMLTableRowElement).cells[index]!.style, {
          width,
          minWidth: width,
          maxWidth: width,
        });
      });
      body.append(clone);
      overlay.append(body);
      overlay.style.setProperty('--divider-width', `${rect.width}px`);
    }
    overlay.removeAttribute('id');
    overlay.removeAttribute('data-pagination-id');
    for (const element of overlay.querySelectorAll('[id]')) {
      element.removeAttribute('id');
    }
    overlay.setAttribute('aria-hidden', 'true');
    overlay.inert = true;
    Object.assign(overlay.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      boxSizing: 'border-box',
      minWidth: '0',
      maxWidth: 'none',
      minHeight: '0',
      maxHeight: 'none',
      margin: '0',
      pointerEvents: 'none',
      visibility: 'visible',
      transition: 'none',
    });
    return {
      id: item.dataset.paginationId!,
      item,
      overlay,
      distance: getComputedStyle(item).getPropertyValue('--space') || '0px',
    };
  }

  private animate(
    { id, item, overlay, distance }: ItemSnapshot,
    entering: boolean,
  ) {
    this.animations.get(id)?.();
    const container = this.getOverlayContainer();
    if (
      !container ||
      typeof overlay.animate !== 'function' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    container.append(overlay);
    if (entering) {
      item.style.visibility = 'hidden';
    }
    const animation = overlay.animate(
      entering
        ? [
            { opacity: 0, transform: `translateX(${distance})` },
            { opacity: 1, transform: 'translateX(0)' },
          ]
        : [
            { opacity: 1, transform: 'translateX(0)' },
            { opacity: 0, transform: `translateX(calc(${distance} * -1))` },
          ],
      {
        duration: PAGINATION_TIME_IN_MILLISECONDS / 2,
        delay: entering ? PAGINATION_TIME_IN_MILLISECONDS / 2 : 0,
        easing: 'ease',
        fill: 'both',
      },
    );
    let mutationObserver: MutationObserver | undefined;
    let resizeObserver: ResizeObserver | undefined;
    if (entering) {
      const refreshOverlay = () => {
        if (!item.isConnected) {
          return;
        }
        const updatedOverlay = this.snapshot(item).overlay;
        overlay.className = updatedOverlay.className;
        overlay.style.cssText = updatedOverlay.style.cssText;
        overlay.replaceChildren(...Array.from(updatedOverlay.childNodes));
      };
      mutationObserver = new MutationObserver(refreshOverlay);
      mutationObserver.observe(item, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(refreshOverlay);
        resizeObserver.observe(item);
      }
    }
    const cleanup = () => {
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      animation.onfinish = null;
      animation.oncancel = null;
      animation.cancel();
      overlay.remove();
      if (entering) {
        item.style.removeProperty('visibility');
      }
      this.animations.delete(id);
    };
    this.animations.set(id, cleanup);
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
  }

  render() {
    const { component, children, className, style } = this.props;
    return createElement(
      component,
      {
        ref: this.containerRef,
        className,
        style,
        'aria-hidden': this.props['aria-hidden'],
      },
      children,
    );
  }
}
